import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import type { HeroSlide } from '@/lib/types';

const FALLBACK_SLIDES: HeroSlide[] = [
  {
    image: "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/603ca32a-3de9-4ccf-97a7-9ef2481f8546_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-5.webp",
    headline: "Empowering Youth Through Faith and Community",
    subtext: "Join us at The Great Multitude Church International in Edea, Cameroon, as we uplift and inspire our youth, fostering hope and change in our community.",
  },
  {
    image: "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/7dd190bd-a977-4941-b5f7-34b8b48612b1_compressed_WhatsApp-Image-2026-09-12-at-15.29.02-3.webp",
    headline: "A Legacy of Service and Faith",
    subtext: "Dedicated to empowering lives, our church fosters hope, compassion, and unity among all members of the community through outreach and love.",
  },
  {
    image: "https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/66b0c0ea-15a5-4160-915f-b54a85c2a275_compressed_WhatsApp-Image-2026-09-12-at-15.29.02-2.webp",
    headline: "Join Us in Making a Difference",
    subtext: "Discover how you can get involved and support our community initiatives through our outreach programs, projects and youth empowerment.",
  },
];

export default function HeroSection() {
  const { data: settings } = useSiteSettings();
  const slides = settings?.hero_images?.length ? settings.hero_images : FALLBACK_SLIDES;

  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setTransitioning(false);
      }, 600);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goTo = (idx: number) => {
    if (idx === current) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(idx);
      setTransitioning(false);
    }, 400);
  };

  const slide = slides[current];

  return (
    <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden" aria-label="Hero">
      {/* Background */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
      >
        <img
          src={slide.image}
          alt={slide.headline}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-4 md:px-8 lg:px-16">
        <div
          className={`max-w-3xl transition-all duration-700 ${
            transitioning ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-accent-500/20 border border-accent-500/40 text-accent-300 text-xs font-label tracking-widest uppercase px-4 py-2 rounded-full mb-6">
            <i className="ri-building-line" aria-hidden="true" />
            {settings?.church_name || 'TGMCI'} — Edea, Cameroon &amp; Accra, Ghana
          </div>

          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
            {slide.headline}
          </h1>

          <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl">
            {slide.subtext}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-primary text-sm px-6 py-3.5">
              <i className="ri-user-add-line" aria-hidden="true" />
              Get Involved
            </Link>
            <Link to="/about" className="btn-outline-white text-sm px-6 py-3.5">
              <i className="ri-information-line" aria-hidden="true" />
              Learn More
            </Link>
            <Link to="/give" className="btn-accent text-sm px-6 py-3.5">
              <i className="ri-heart-line" aria-hidden="true" />
              Support TGMCI
            </Link>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-10 left-0 right-0 z-10 flex justify-center gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => goTo(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`cursor-pointer transition-all duration-300 rounded-full ${
              idx === current
                ? 'bg-accent-500 w-8 h-2'
                : 'bg-white/40 hover:bg-white/60 w-2 h-2'
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-8 z-10 hidden md:flex flex-col items-center gap-2">
        <span className="text-white/50 text-xs font-label tracking-widest uppercase -rotate-90 origin-center mb-4">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" />
      </div>

      {/* Overlay Pattern */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(255,255,255,0.03) 2px,
            rgba(255,255,255,0.03) 4px
          )`,
        }}
      />
    </section>
  );
}